import { useImperativeHandle, useState, forwardRef, useEffect } from 'react';
import { DetalhesPrint, Documentos } from './style';
import { Button, Modal, Row, Space, Spin, Table, Tooltip } from 'antd';
import { IDetalheAgendamento } from '../../services';
import { CloseOutlined, PrinterOutlined } from '@ant-design/icons';

import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

import anexo from '../../../../../../assets/img/anexar.svg';
import iconcoleta from '../../../../../../assets/img/coleta.svg';
import iconentrega from '../../../../../../assets/img/entrega.svg';
import iconAgendado from '../../../../../../assets/img/agendado.svg';
import iconExpedido from '../../../../../../assets/img/expedido.svg';

import { ColumnType } from 'antd/lib/table';
import mask from 'utils/mask';
import api from 'service/api';
import { SKW_PREFIX } from 'utils/constants';
import { ApplicationState } from 'store';
import saveAs from 'file-saver';
import { getPedidosOrdemCarga } from '../../services';
import TextTable from 'components/TextTable';
import TitleTable from 'components/TitleTable';

interface Props {
  detalhesAgendamento: IDetalheAgendamento;
}

export interface PropsRef {
  showModal: () => void;
}

interface IPropsDocumento {
  nuNota: number;
  numNota: number;
  codFornecedor: number;
  codDestinatario: number;
  pesoBruto: number;
  valor: number;
  destinatario: string;
  fornecedor: string;
  observacao: string;
  cidadeOrigem: string;
  qtdVolume: number;
  tipoVolume: string;
  nunotaEmitida: number;
  produto: string;
  nomeCD: string;
  temAnexo: number;
}

const ModalDetalhesAgendamento: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<PropsRef>
> = forwardRef<PropsRef, Props>(({ detalhesAgendamento }, ref) => {
  const token = useSelector((state: ApplicationState) => state.auth.token);
  const parceiros = useSelector((state: ApplicationState) => state.auth?.inscricaoAtiva);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [pedidosOrdemCarga, setPedidosOrdemCarga] = useState<IPropsDocumento[]>([]);


  useEffect(() => {
    let controller = new AbortController();
    setLoading(true);
    if (detalhesAgendamento) {
      try {
        setLoading(true);
        getPedidosOrdemCarga
          .getPedidosOrdemCarga(
            detalhesAgendamento.ordemCarga,
            detalhesAgendamento.codEmp,
            parceiros
          )
          .then((res) => {
            setLoading(false);
            const { pedidos } = res.data;
            setPedidosOrdemCarga(pedidos);
          });

      } catch (error: any) {
        setLoading(false);
        if (error.response.data.Error)
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response.data.Error,
          });
        else
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'Erro ao carregar a lista de documentos!',
          });

        setPedidosOrdemCarga([]);
      }
    }
    return () => {
      controller.abort();
    };
  }, [detalhesAgendamento, isModalVisible, parceiros]);

  function downloadAnexos(pedidosOrdemCarga: any, detalhesAgendamento: any) {
    pedidosOrdemCarga.map((item: any, index: number) => {
      return (
        item?.temAnexo &&
        api
          .post(
            `call?serviceName=${SKW_PREFIX}baixarAnexoNota&token=${token}`,
            {
              nunota: item?.nuNota,
            },
            {
              responseType: 'arraybuffer',
            }
          )
          .then((res) => {
            const blob = new Blob([res.data], {
              type: 'application/octet-stream',
            });
            const filename = 'nota-agendamento.zip';
            saveAs(blob, filename);
            Swal.fire({
              title: `Download efetuado com sucesso!`,
              html: '',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500,
            });
          })
          .catch((err) => {
            Swal.fire({
              title: `Não foi possível efetuar o download.`,
              html: '',
              icon: 'error',
              showConfirmButton: true,
              timer: 2000,
            });
          })
      );
    });
  }

  const handlePdf = ({ codAgendamento }: any) => {
    api
      .get(
        `/call?serviceName=${SKW_PREFIX}imprimeAgendamentoDetalhado&token=${token}&agendamento=${codAgendamento}`,
        {
          responseType: 'arraybuffer',
        }
      )
      .then((res) => {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        saveAs(blob, `AGENDAMENTO_${codAgendamento}.pdf`);
      });
  };

  const columns: ColumnType<any>[] = [
    {
      title: <TitleTable>Nº da NF</TitleTable>,
      dataIndex: 'nuNota',
      key: 'nuNota',
      width: 100,
      render: (text) => (text ? <TextTable>{text}</TextTable> : 'Não localizado'),
    },
    {
      title: <TitleTable>Fornecedor</TitleTable>,
      dataIndex: 'fornecedor',
      key: 'fornecedor',
      width: 250,
      render: (text) => (text ? <TextTable>{mask('camelCase', text)}</TextTable> : 'Não informado'),
    },
    {
      title: <TitleTable>Peso Bruto</TitleTable>,
      dataIndex: 'pesoBruto',
      key: 'pesoBruto',
      width: 150,
      render: (text) => {
        return text ? <TextTable>{mask('peso', text)}</TextTable> : 'Não informado';
      },
    },
    {
      title: <TitleTable>Valor</TitleTable>,
      dataIndex: 'valor',
      key: 'valor',
      width: 150,
      render: (text) => {
        return text ? <TextTable>{mask('currency', text)}</TextTable> : 'Não informado';
      },
    },

    {
      title: <TitleTable>Observação</TitleTable>,
      dataIndex: 'observacao',
      key: 'observacao',
      render: (text) =>
        text ? (
          <TextTable>
            <Tooltip title={mask('camelCase', text)}>
              <span
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {mask('camelCase', text)}
              </span>
            </Tooltip>
          </TextTable>
        ) : (
          'Sem obs:'
        ),
    },
  ];

  useImperativeHandle(
    ref,
    () => ({
      showModal: () => setIsModalVisible(true),
    }),
    [setIsModalVisible]
  );



  return (
    <Modal
      centered
      title={
        <span
          style={{ color: '#1890FF', fontSize: '20px' }}
        >{`Detalhes do agendamento nº ${detalhesAgendamento?.codAgendamento}`}</span>
      }
      visible={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      maskClosable={true}
      closable={true}
      destroyOnClose={true}
      closeIcon={<CloseOutlined />}
      keyboard={true}
      width={1200}
      footer={[]}
    >
      <DetalhesPrint>
        <Spin spinning={false} />
        <div>
          <p>
            Tipo de Agendamento
            <div>
              {detalhesAgendamento?.tipoWms ? detalhesAgendamento?.tipoWms === 'EXP' ? (
                <>
                  {' '}
                  <img src={iconcoleta} alt="Coleta" title="Coleta" />{' '}
                  <span style={{ color: '#7CB305' }}>Coleta</span>
                </>
              ) : (
                <>
                  {' '}
                  <img src={iconentrega} alt="Entrega" title="Entrega" />{' '}
                  <span style={{ color: '#F23C3C' }}>Entrega</span>
                </>
              ) : "Não informado"}
            </div>
          </p>

          <p>
            Transportadora{' '}
            <div>{detalhesAgendamento?.transportadora ? mask('transportadoraUf', detalhesAgendamento?.transportadora) : "Não informado"}</div>
          </p>

          <p>
            Motorista<div>{detalhesAgendamento?.motorista ? mask('camelCase', detalhesAgendamento?.motorista) : "Não informado"}</div>
          </p>

          <p>
            Veículo
            <div>
              {detalhesAgendamento?.placa ? detalhesAgendamento?.placa?.toUpperCase() + " - " + mask('camelCase', detalhesAgendamento?.detalheVeiculo?.tipoCarroceria) : "Não informado"}
            </div>
          </p>
        </div>

        <div>
          <p>
            Status
            <div>
              {detalhesAgendamento?.status ? detalhesAgendamento?.status === 'AGE' ? (
                <>
                  {' '}
                  <img
                    src={iconAgendado}
                    alt="Agendado"
                    title="Agendado"
                    style={{ width: '18px' }}
                  />{' '}
                  <span style={{ color: '#B45309' }}>Agendado</span>
                </>
              ) : (
                <>
                  {' '}
                  <img
                    src={iconExpedido}
                    alt="Expedido"
                    title="Expedido"
                    style={{ width: '18px' }}
                  />{' '}
                  <span style={{ color: '#1890FF' }}>Expedido</span>
                </>
              ) : "Não informado"}
            </div>
          </p>
          <p>
            Nome do Cliente <div>{detalhesAgendamento?.cliente ? mask('camelCase', detalhesAgendamento?.cliente) : "Não informado"}</div>
          </p>
          <p>
            Data do agendamento
            <div>{detalhesAgendamento?.dhAgendamento ? detalhesAgendamento?.dhAgendamento?.toString().split(' ')[0] : "Não informado"}</div>
          </p>
          <p>
            Hora do agendamento
            <div>{detalhesAgendamento?.dhAgendamento ? detalhesAgendamento?.dhAgendamento?.toString().split(' ')[1] : "Não informado"}</div>
          </p>
        </div>

        <div>
          <p>
            Tipo de Carga
            <div>{detalhesAgendamento?.tipoCarga ? detalhesAgendamento?.tipoCarga === 'F' ? ' Batida' : ' Paletizada' : "Não informado"}</div>
          </p>
          <p>
            Tipo de Volume <div> {detalhesAgendamento?.volume ? mask('camelCase', detalhesAgendamento?.volume) : "Não informado"}</div>
          </p>
          <p>
            Valor <div>{detalhesAgendamento?.valor ? mask('currency', detalhesAgendamento?.valor) : "Não informado"}</div>
          </p>
          <p>
            Peso <div>{detalhesAgendamento?.pesoBruto ? mask('pesoTonelada', detalhesAgendamento?.pesoBruto) : "Não informado"}</div>
          </p>
        </div>

        <div>
          <p>
            Chegada no CD

            <div>{detalhesAgendamento?.dhChegada ? detalhesAgendamento?.dhChegada : "Não informado"}</div>
          </p>
          <p>
            Entrada no CD
            <div>{detalhesAgendamento?.dhEntrada ? detalhesAgendamento?.dhEntrada : "Não informado"}</div>
          </p>
          <p>
            Início da expedição
            <div>{detalhesAgendamento?.dhIniOper ? detalhesAgendamento?.dhIniOper : "Não informado"}</div>
          </p>
          <p>
            Fim da expedição
            <div>{detalhesAgendamento?.dhFinOper ? detalhesAgendamento?.dhFinOper : "Não informado"}</div>
          </p>
          <p>
            Saída do CD
            <div>{detalhesAgendamento?.dhSaida ? detalhesAgendamento?.dhSaida : "Não informado"}</div>
          </p>
        </div>
      </DetalhesPrint>

      <Documentos>
        <h2 style={{ marginTop: '20px' }}>Itens</h2>

        <Table
          style={{ fontSize: 10, display: 'inline' }}
          columns={columns}
          scroll={{ x: 900 }}
          pagination={false}
          size={'large'}
          className="table"
          dataSource={pedidosOrdemCarga}
          loading={loading}
        />
      </Documentos>


      <Row
        style={{
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
        }}
      >
        <Space>
          {detalhesAgendamento?.temAnexo && (
            <img
              src={anexo}
              alt="anexos"
              title="anexos"
              onClick={() => downloadAnexos(pedidosOrdemCarga, detalhesAgendamento)}
              style={{ cursor: 'pointer', width: '30px' }}
            />
          )}

          <Button
            type="ghost"
            block
            className="primary"
            icon={<PrinterOutlined />}
            onClick={() => handlePdf(detalhesAgendamento)}
          >
            Imprimir
          </Button>
          <Button key="back" size="middle" onClick={() => setIsModalVisible(false)}>
            Fechar
          </Button>
        </Space>
      </Row>
    </Modal>
  );
});

export { ModalDetalhesAgendamento };
